import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import { AdminPageHeader } from '../Common/AdminPagesHeader';
import {
    CUSTOMER_LIST,
    CUSTOMER_COLUMNS,
    ROLES,
    LOCAL_STORAGE,
    QUERY_KEYS,
    DATE_TIME_FORMATS,
} from '../../../constants';
import { Table, AddNewCustomer, ResponsiveButton } from '../../shared';
import SSCSelect from '../../shared/SSCSelect';
import { usePagination } from '../../../hooks';
import {
    getAllCustomer,
    popup,
    getLocalStorageItem,
    getAllUsers,
    downloadCSV,
} from '../../../utils';
import { updateUserVisitPageAPI } from '../../../utils/apiServices/userReportAPI';
import { clearCSMCaseload, setCSMCaseload, setSidePanel } from '../../../redux';
import { allowToViewCSMCaseload, showAddClientButton } from '../../../constants/permissions';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';
import { CSVIcon } from '../../../assets/svg';
import { clientCsvHeaderMap } from '../ScoreCardReportAdmin/ScoreCardsFunctions';

export const CustomerListing = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const csmCaseloadData = useSelector((s) => s.csmCaseload?.csmCaseloadData);
    const role = useSelector((s) => s.user.role);
    const userId = getLocalStorageItem(LOCAL_STORAGE.USER_ID);
    const [serverSort, setServerSort] = useState({ column: 'updated_at', sort: 'desc' });
    const [response, setResponse] = useState({ data: [], count: 0 });
    // Status filter: all | active | archived
    const [statusFilter, setStatusFilter] = useState('all');
    const [regionFilter, setRegionFilter] = useState('');
    const [industryFilter, setIndustryFilter] = useState('');

    // Audit logging (page visits)
    const visitRef = useRef(null);
    const selectedCSM = csmCaseloadData
        ? {
              label: csmCaseloadData?.full_name,
              value: csmCaseloadData,
          }
        : null;

    const [csmSearch, setCsmSearch] = useState('');
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const pagination = usePagination(response.data.length, response.count);
    const [showAddModal, setShowAddModal] = useState(false);

    const handleAddClick = () => {
        setShowAddModal(true);
    };

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const customerQuery = useQuery({
        queryKey: [
            QUERY_KEYS.CUSTOMERS,
            pagination.offset,
            pagination.limit,
            debouncedSearch,
            selectedCSM?.value?.id,
            serverSort.column,
            serverSort.sort,
            statusFilter,
            regionFilter,
            industryFilter
        ],
        queryFn: async () => {
            const filter = {};
            if (statusFilter === 'active') filter.status = true;
            if (statusFilter === 'archived') filter.status = false;
            if (regionFilter && regionFilter.trim()) filter.region = regionFilter.trim();
            if (industryFilter && industryFilter.trim()) filter.industry = industryFilter.trim();
            const hasFilter = Object.keys(filter).length > 0;
            return await getAllCustomer({
                offset: pagination.offset,
                limit: pagination.limit,
                search: debouncedSearch,
                id: selectedCSM?.value?.id || '',
                role: selectedCSM?.value?.role_code || '',
                withPrimaryUsers: true,
                withPlatforms: true,
                orderBy: serverSort,
                filter: hasFilter ? filter : undefined,
            });
        },
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
    });
    if (customerQuery.isError) popup('error', customerQuery.error.message);

    useEffect(() => {
        if (customerQuery.data && customerQuery.isFetched) {
            setResponse(customerQuery.data);
        }
    }, [customerQuery.data, customerQuery.isFetched]);

    const [csmDebouncedSearch] = useDebouncedValue(csmSearch, 500);
    const csmQuery = useQuery({
        queryKey: [QUERY_KEYS.CSM_USER, csmDebouncedSearch, '25'],
        queryFn: () =>
            getAllUsers({
                limit: 25,
                offset: 1,
                search: csmDebouncedSearch,
                filter: { status: true },
                role: [ROLES.CSM],
            }),
        select: (data) => data.data.filter((csm) => csm.id !== userId),
    });

    useEffect(() => {
        return () => dispatch(setSidePanel({ sidePanel: false, id: '' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSelectChange = (data) => {
        if (data === null || data === undefined) {
            dispatch(clearCSMCaseload());
        } else {
            dispatch(setCSMCaseload({ csmCaseloadData: data?.value, isCsmCaseload: true }));
        }
    };

    const allCustomerQuery = useQuery({
        queryKey: [QUERY_KEYS.ALL_CUSTOMERS, selectedCSM?.value?.id],
        queryFn: async () =>
            await getAllCustomer({
                id: selectedCSM?.value?.id || '',
                role: selectedCSM?.value?.role_code || '',
                withPrimaryUsers: false,
                withPlatforms: true,
            }),
        enabled: false,
    });

    const onCSVExportClick = async () => {
        const { data } = await allCustomerQuery.refetch();
        downloadCSV({
            data: data.data,
            headersMap: clientCsvHeaderMap,
            filename: `clients_report_${moment().format(DATE_TIME_FORMATS.DATE_TIME_UNDERSCORE)}`,
            arrayFieldMap: { platforms: 'name' },
        });
    };

    // Page visit logging for admin accounts list
    useEffect(() => {
        const path = window.location.pathname;
        const uid = userId;
        const send = async (data = {}) => {
            try {
                await updateUserVisitPageAPI({ accountId: undefined, userId: uid, path, data });
            } catch (e) {
                // non-fatal analytics failure
                console.warn('visit logging failed', e);
            }
        };
        visitRef.current = send;
        // entry
        visitRef.current({ entry_time: moment.utc().toISOString() });
        return () => {
            // exit
            visitRef.current && visitRef.current({ exit_time: moment.utc().toISOString() });
            visitRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Column presets per role (saved to localStorage; auto-load)
    const baseColumns = [ROLES.ADMIN, ROLES.SUPER_ADMIN].includes(role)
        ? CUSTOMER_COLUMNS
        : CUSTOMER_COLUMNS.filter((columns) => columns.name !== 'Created By');
    const presetKey = `accounts_columns_preset_${role || 'unknown'}`;
    const defaultPreset = baseColumns.map((c) => c.name);
    const savedPresetRaw = typeof window !== 'undefined' ? window.localStorage.getItem(presetKey) : null;
    let presetNames;
    try {
        presetNames = savedPresetRaw ? JSON.parse(savedPresetRaw) : defaultPreset;
    } catch {
        presetNames = defaultPreset;
    }
    useEffect(() => {
        try {
            if (!savedPresetRaw) {
                window.localStorage.setItem(presetKey, JSON.stringify(defaultPreset));
            }
        } catch (e) {
            console.warn('preset save failed', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);
    const finalColumns = presetNames
        .map((name) => baseColumns.find((c) => c.name === name))
        .filter(Boolean);

    return (
        <>
            <AdminPageHeader
                heading={CUSTOMER_LIST.PAGE_HEADING}
                buttonText={CUSTOMER_LIST.BUTTON_TEXT}
                searchBarPlaceholder={CUSTOMER_LIST.SEARCH_BAR_PLACEHOLDER}
                setSearch={setSearch}
                count={pagination.dataCount}
                showAddButton={showAddClientButton(role)}
                handleAddClick={handleAddClick}
                AddModal={
                    showAddModal ? (
                        <AddNewCustomer
                            showAddModal={showAddModal}
                            setShowAddModal={setShowAddModal}
                            debouncedApiCall={() => {}}
                        />
                    ) : (
                        ''
                    )
                }
                setCurrentPage={pagination.setCurrentPage}
                backbutton
                searchAdjacentChildren={
                    <>
                        {allowToViewCSMCaseload(role) && (
                            <div className='col-12 col-md-5 col-xxl-4'>
                                <SSCSelect
                                    className={'w-100 w-md-50'}
                                    options={csmQuery.data?.map((csm) => ({
                                        label: csm.full_name,
                                        value: csm,
                                    }))}
                                    placeholder={'Apply CSM caseload...'}
                                    onChange={onSelectChange}
                                    onInputChange={(v) => {
                                        setCsmSearch(v);
                                    }}
                                    isLoading={csmQuery.isFetching}
                                    isClearable={true}
                                    value={selectedCSM}
                                />
                            </div>
                        )}
                        <div className='col-12 col-md-4 col-xxl-3'>
                            <SSCSelect
                                className={'w-100 w-md-50'}
                                options={[
                                    { label: 'All Status', value: 'all' },
                                    { label: 'Active', value: 'active' },
                                    { label: 'Archived', value: 'archived' },
                                ]}
                                placeholder={'Filter by status...'}
                                onChange={(opt) => setStatusFilter(opt?.value || 'all')}
                                isClearable={true}
                                value={{ label: statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1), value: statusFilter }}
                            />
                        </div>
                        <div className='col-12 col-md-4 col-xxl-3'>
                            <input
                                type='text'
                                className='form-control'
                                placeholder='Region'
                                value={regionFilter}
                                onChange={(e) => setRegionFilter(e.target.value)}
                            />
                        </div>
                        <div className='col-12 col-md-4 col-xxl-3'>
                            <input
                                type='text'
                                className='form-control'
                                placeholder='Industry'
                                value={industryFilter}
                                onChange={(e) => setIndustryFilter(e.target.value)}
                            />
                        </div>
                    </>
                }
                headerButtonChildren={
                    <ResponsiveButton
                        title='Export Clients as CSV'
                        onClick={onCSVExportClick}
                        disabled={allCustomerQuery.isFetching}
                        breakpoint={575}
                        icon={<CSVIcon currentColor />}
                        leftIcon={<CSVIcon currentColor />}
                        label={'Export as CSV'}
                        isLoading={allCustomerQuery.isFetching}
                        isWhite
                    />
                }
            />
            <Table
                columns={finalColumns}
                data={response.data}
                pagination={pagination}
                pageName={CUSTOMER_LIST.PAGE_NAME}
                search={search}
                debouncedApiCall={() => {}}
                stickyColumns={['Client Name']}
                isLoading={customerQuery.isFetching}
                setServerSort={setServerSort}
                columnsForServerSort={['name', 'email', 'created_at', 'csm_name', 'status']}
            />
        </>
    );
};
